import { useMemo } from 'react';

interface RegressionResult {
  m: number;
  b: number;
  stdErr: number;
  predict: (x: number) => number;
}

function linearRegression(values: number[]): RegressionResult {
  const n = values.length;
  if (n < 2) return { m: 0, b: values[0] || 0, stdErr: 0, predict: () => values[0] || 0 };

  const xs    = Array.from({ length: n }, (_, i) => i);
  const sumX  = xs.reduce((a, x) => a + x, 0);
  const sumY  = values.reduce((a, y) => a + y, 0);
  const sumXX = xs.reduce((a, x) => a + x * x, 0);
  const sumXY = xs.reduce((a, x, i) => a + x * values[i], 0);
  const denom = n * sumXX - sumX * sumX;

  const m  = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  const b  = (sumY - m * sumX) / n;

  const predictions = xs.map((x) => m * x + b);
  const sse         = values.reduce((a, v, i) => a + (v - predictions[i]) ** 2, 0);
  const stdErr      = Math.sqrt(sse / Math.max(n - 2, 1));

  return { m, b, stdErr, predict: (x: number) => m * x + b };
}

/**
 * useForecast
 * Appends N forecast points to `data` with `_forecast`, `_lo`, `_hi` keys.
 * The trendline values (one per existing point) are also returned for overlay.
 *
 * @param data     Original chart data array
 * @param yKeys    Data keys to forecast (e.g. ['openCapas', 'closedCapas'])
 * @param enabled  Whether forecast is active
 * @param periods  How many future points to add (default 3)
 */
export function useForecast(
  data: Record<string, unknown>[],
  yKeys: string[],
  enabled: boolean,
  periods = 3,
) {
  return useMemo(() => {
    if (!enabled || data.length < 3 || yKeys.length === 0) {
      return { forecastData: data, trendValues: {} };
    }

    const regs: Record<string, RegressionResult> = {};
    yKeys.forEach((key) => {
      const values = data.map((d) => Number(d[key] ?? 0));
      regs[key] = linearRegression(values);
    });

    // Trendline values aligned with original data (for overlay line)
    const trendValues: Record<string, number[]> = {};
    yKeys.forEach((key) => {
      trendValues[key] = data.map((_, i) => Math.max(0, Math.round(regs[key].predict(i))));
    });

    // Append forecast points
    const extended = data.map((row, i) => {
      const extra: Record<string, unknown> = {};
      yKeys.forEach((key) => {
        extra[`${key}_trend`] = Math.max(0, Math.round(regs[key].predict(i)));
      });
      return { ...row, ...extra };
    });

    for (let p = 1; p <= periods; p++) {
      const x    = data.length - 1 + p;
      const row: Record<string, unknown> = { _forecast: true };
      // Carry forward the x-axis label pattern
      const lastLabel = String(data[data.length - 1]?.label ?? data[data.length - 1]?.month ?? '');
      row.label = `+${p}`;
      row.month = lastLabel ? `f+${p}` : `+${p}`;

      yKeys.forEach((key) => {
        const { predict, stdErr } = regs[key];
        const pred = predict(x);
        row[`${key}_trend`]    = Math.max(0, Math.round(pred));
        row[`${key}_forecast`] = Math.max(0, Math.round(pred));
        row[`${key}_lo`]       = Math.max(0, Math.round(pred - 1.96 * stdErr));
        row[`${key}_hi`]       = Math.max(0, Math.round(pred + 1.96 * stdErr));
      });

      extended.push(row);
    }

    return { forecastData: extended, trendValues };
  }, [data, yKeys, enabled, periods]);
}
